// Copyright (c) Docugami, Inc. All rights reserved.

import { getPageForPos } from "sdv/Page";
import { IResources } from "sdv/Resources";
import { BoundsComponent } from "sdv/components/BoundsComponent";
import { ControlComponent, ControlType, postOrderTraversal } from "sdv/components/ControlComponent";
import { CursorComponent } from "sdv/components/CursorComponent";
import { CursorHitComponent } from "sdv/components/CursorHitComponent";
import { DragComponent } from "sdv/components/DragManipulationComponent";
import { InteractableComponent } from "sdv/components/InteractableComponent";
import { SnapToTokenComponent } from "sdv/components/SnapToTokenComponent";
import { TokenLine, TokenLinesComponent, TokenRangeBox } from "sdv/components/TokenLinesComponent";
import { TokenPositionComponent } from "sdv/components/TokenPositionComponent";
import { ComponentId } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { createCursor } from "sdv/engine/EntityFactory";
import { ActionId } from "sdv/input/Actions";
import { IInput } from "sdv/input/InputController";
import { areaOfBox, distanceFromBoxSquared, distanceFromBoxYPlane, getOverlappingEditTokens, pointInBox, pointInBoxes } from "store/shared_types";
import { ISystem, Queries } from "../engine/Engine";

/**
 * Performs all the initial hit testing for the scene. This includes interactables,
 * token positions, chunks, etc.
 */
export class ControlHitTestSystem implements ISystem {
    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;
    input!: IInput

    queries: Queries = {
        rootControl: { types: [ComponentId.RootControl, ComponentId.Active] },
        cursor: { types: [ComponentId.Cursor] },
        cursorHit: { types: [ComponentId.CursorHit] },
        interactables: { types: [ComponentId.Interactable] },
        dragManipulations: { types: [ComponentId.Drag] },
        tokenPositions: { types: [ComponentId.Drag, ComponentId.TokenPosition] },
        snapTokenPositions: { types: [ComponentId.Drag, ComponentId.TokenPosition, ComponentId.SnapToToken] },
        tokenLines: { types: [ComponentId.TokenLines] }
    }

    update() {
        const cursor = this.queries.cursor.query!.entities[0];
        if (!cursor) {
            createCursor(this.entities);
            return;
        }

        const cursorComponent = cursor.getComponent<CursorComponent>(ComponentId.Cursor)!;
        const newPoint = this.input.getAxis2d(ActionId.SelectCursor) ?? undefined;
        if (newPoint?.x !== cursorComponent.point?.x || newPoint?.y !== cursorComponent.point?.y) {
            this.queries.cursorHit.query!.entities.forEach(e => e.removeComponent(ComponentId.CursorHit));

            cursorComponent.point = newPoint;
            delete cursorComponent.controlEntityName;
            delete cursorComponent.currentPageId;
            delete cursorComponent.token;
            delete cursorComponent.tokenLine;

            if (!!cursorComponent.point) {
                cursorComponent.currentPageId = getPageForPos(this.resources.get("visiblePages").value, cursorComponent.point.y)?.id;
            }

            const line = this.getTokenLineForCursor();
            if (!!line) {
                cursorComponent.tokenLine = line;
                const token = this.getTokenForCursorFromLine(line)
                if (token !== null) {
                    cursorComponent.token = token;
                    cursorComponent.editTokens = getOverlappingEditTokens(token, this.resources.get("pageTokens").value);
                }
            }

            const controlHit = this.hitTestControls();
            cursorComponent.controlEntityName = controlHit?.name;

            const interactableHit = this.hitTestInteractables();
            const hitEntity = interactableHit ?? controlHit;

            hitEntity?.addComponent<CursorHitComponent>({
                typeId: ComponentId.CursorHit,
                point: cursorComponent.point!
            });

            this.updateDragManipulations();
            this.updateTokenPositions(cursorComponent.token);

            this.updateTokenSnapPositions();
        }
    }

    private updateTokenPositions(token?: number) {
        for (const entity of this.queries.tokenPositions.query!.entities) {
            const tokenPositionComponent = entity.getComponent<TokenPositionComponent>(ComponentId.TokenPosition)!;
            tokenPositionComponent.prevToken = tokenPositionComponent.token;
            tokenPositionComponent.token = token ?? undefined;
        }
    }

    private updateTokenSnapPositions() {
        const entities = this.queries.snapTokenPositions.query!.entities;
        for (const entity of entities) {
            const tokenPosition = entity.getComponent<TokenPositionComponent>(ComponentId.TokenPosition)!;
            const snapInfo = entity.getComponent<SnapToTokenComponent>(ComponentId.SnapToToken)!;

            let snapPosition: number | undefined = tokenPosition.token;
            if (!!snapInfo.minTok && snapPosition !== undefined) {
                snapPosition = Math.max(snapInfo.minTok, snapPosition);
            }

            if (!!snapInfo.maxTok && snapPosition !== undefined) {
                snapPosition = Math.min(snapInfo.maxTok, snapPosition);
            }

            tokenPosition.token = snapPosition;
        }
    }

    private hitTestInteractables(): Entity | null {
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        if (!!cursor) {
            for (const interactableEntity of this.queries.interactables.query!.entities) {
                const interactable = interactableEntity.getComponent<InteractableComponent>(ComponentId.Interactable)!;
                if (!interactable.disabled && pointInBox(cursor.x, cursor.y, interactable.hitBox)) {
                    return interactableEntity;
                }
            }
        }

        return null;
    }

    private hitTestControls(): Entity | null {
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        const rootEntity = this.queries.rootControl.query!.entities[0];
        if (!!rootEntity && !!cursor) {
            // Walk the candidate controls (from deepest to shallowest) and look for:
            // 1. A chunk whose bounds directly contains the point
            // 2. Otherwise, pick the shallowest control with bounding box that approximates the smallest area
            const iterator = postOrderTraversal(rootEntity, this.entities);
            let result = iterator.next();

            let hitControl: Entity | null = null;
            let smallestArea = Infinity;
            let areaControl: Entity | null = null
            while (!!result.value) {
                const entity: Entity = result.value;
                const c = entity.getComponent<ControlComponent>(ComponentId.Control)!;
                const boundsComp = entity.getComponent<BoundsComponent>(ComponentId.Bounds);
                if (!!c.parent && !!boundsComp?.collisionEnabled) {
                    if (pointInBoxes(cursor.x, cursor.y, boundsComp.bounds)) {
                        hitControl = entity;
                        break;
                    }
                    else if (c.type === ControlType.LargeChunk && pointInBoxes(cursor.x, cursor.y, boundsComp.containerBoxes)) {
                        // Find the last parent that shares a similar bounding box area
                        const area = areaOfBox(boundsComp.boundingBox);
                        if (area <= smallestArea * 1.01) {
                            smallestArea = area;
                            areaControl = entity;
                        }
                    }
                }

                result = iterator.next();
            }

            return hitControl ?? areaControl;
        }

        return null;
    }

    private updateDragManipulations() {
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        if (!cursor) {
            return;
        }

        const dragEntities = this.queries.dragManipulations.query!.entities;
        if (dragEntities.length > 0) {
            for (const drag of dragEntities) {
                const comp = drag.getComponent<DragComponent>(ComponentId.Drag)!;
                comp.cursor = cursor;
            }
        }
    }

    private getTokenLineForCursor(): TokenLine | null {
        const cursorComp = this.queries.cursor.query!.single()?.getComponent<CursorComponent>(ComponentId.Cursor)!;
        const tokenLinesComp = this.queries.tokenLines.query!.single()?.getComponent<TokenLinesComponent>(ComponentId.TokenLines)!;

        // Ensure the cursor is valid and within the bounds of a visible page
        let tokenLine: TokenLine | null = null;
        if (!!cursorComp?.currentPageId && !!cursorComp.point && !!tokenLinesComp?.tokenLines[cursorComp.currentPageId]) {
            const tokenLines = tokenLinesComp.tokenLines[cursorComp.currentPageId];

            let closestDistance: number = Infinity;
            let closestIndex = -1;
            for (let i = 0; i < tokenLines.length; ++i) {
                const line = tokenLines[i];
                if (pointInBox(cursorComp.point.x, cursorComp.point.y, line.box)) {
                    // Cursor is contained in the token line
                    return line;
                }
                else {
                    // Find the closest line
                    const intersectDist = distanceFromBoxSquared(line.box, cursorComp.point.x, cursorComp.point.y);
                    if (intersectDist < closestDistance) {
                        closestDistance = intersectDist;
                        closestIndex = i;
                    }
                }
            }

            // Using the closest line as a starting point (this gets us into the correct column for instance)
            // now walk the lines in both directions checking the distance from the line in the Y-plane.
            // This ensures that we don't "skip" short lines just because we are closer to the above line.
            if (closestIndex !== -1) {
                let targetIndex = closestIndex;
                tokenLine = tokenLines[targetIndex];
                let curDistanceY = distanceFromBoxYPlane(tokenLine.box, cursorComp.point.y);
                let value = 1;

                const checkIndex = (index: number): boolean => {
                    const line = tokenLines[index];
                    if (!!line) {
                        const checkDistanceY = distanceFromBoxYPlane(line.box, cursorComp.point!.y);
                        if (checkDistanceY < curDistanceY) {
                            curDistanceY = checkDistanceY;
                            return true;
                        }
                    }

                    return false;
                }

                while (targetIndex + value < tokenLines.length || targetIndex - value >= 0) {
                    if (checkIndex(targetIndex + value)) {
                        tokenLine = tokenLines[targetIndex + value];
                    }
                    else if (checkIndex(targetIndex - value)) {
                        tokenLine = tokenLines[targetIndex - value]
                    }
                    else {
                        break;
                    }

                    value++;
                }
            }
        }

        return tokenLine;
    }

    private getTokenForCursorFromLine(line: TokenRangeBox): number | null {
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        let targetToken: number | null = null;
        if (!!cursor) {
            // Find the closest token
            const tokens = this.resources.get("pageTokens").value;
            let dist: number = 100;
            for (let token = line.tokenRange.fromTok; token < line.tokenRange.toTok; ++token) {
                const tokenBox = tokens[token];
                if (!!tokenBox) {
                    if (pointInBox(cursor.x, cursor.y, tokenBox)) {
                        targetToken = token;
                        break;
                    }
                    else {
                        const curDist = distanceFromBoxSquared(tokenBox, cursor.x, cursor.y);
                        if (curDist < dist) {
                            dist = curDist;
                            targetToken = token;
                        }
                    }
                }
            }
        }

        return targetToken;
    }
}
